import {
  AvatarIcon,
  ButtonLink,
  DateTime,
  DeleteIcon,
  IconButton,
  Money,
  SearchIcon,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { Route } from '../../../routes/account.payouts_.$payoutId';
import { usePayoutQuery } from '../../../api/payouts';
import { TransferState } from './TransferState';
import { useState } from 'react';
import { ApiPayoutTransfer } from '../../../openapi';
import { DeleteTransferDialog } from './DeleteTransferDialog';
import {
  formatIbanForDisplay,
  mapNetworkToFullName,
  mapStrangeServerMoneyToServerMoney,
} from '@razz-team/utils';

export function PayoutTable({ editable }: { editable: boolean }) {
  const { t } = useTranslation();
  const { createdAt } = Route.useSearch();
  const { payoutId } = Route.useParams();
  const payoutQuery = usePayoutQuery({ payoutId, createdAt });
  const [selectedTransferToDelete, setSelectedTransferToDelete] =
    useState<ApiPayoutTransfer | null>(null);

  const getTo = (transfer: ApiPayoutTransfer) => {
    switch (transfer.type) {
      case 'Internal':
        return (
          <Stack direction="column">
            <Typography variant="caption">{t('{{companyName}} user')}</Typography>
            <Typography variant="caption" color="text.secondary">
              @{transfer.toAccount.username}
            </Typography>
          </Stack>
        );
      case 'Crypto':
        if (transfer.toAccount.network) {
          return (
            <Stack direction="column">
              <Typography variant="caption">{`${mapNetworkToFullName(transfer.toAccount.network)}`}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
              >{`${transfer.toAccount.address}`}</Typography>
            </Stack>
          );
        }
        return null;
      case 'Fiat':
        return (
          <Stack direction="column">
            <Typography variant="caption">{transfer.toAccount.beneficiaryName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('IBAN {{iban}}', { iban: formatIbanForDisplay(transfer.toAccount.accountNumber) })}
            </Typography>
          </Stack>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>{t('From')}</TableCell>
            <TableCell>{t('To')}</TableCell>
            <TableCell>{t('Amount')}</TableCell>
            <TableCell>{t('Status')}</TableCell>
            <TableCell>{t('Date')}</TableCell>
            {editable ? <TableCell width="5%">{t('Actions')}</TableCell> : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {payoutQuery.data?.transfers.length === 0 && (
            <TableRow>
              <TableCell colSpan={editable ? 7 : 6}>
                <Stack spacing={6} direction="column" alignItems="center" paddingY={12}>
                  <AvatarIcon icon={<SearchIcon />} size="large" />
                  <Typography color="text.secondary">{t('No payments yet')}</Typography>
                  <ButtonLink
                    color="secondary"
                    fullWidth={false}
                    to={'/account/payouts/$payoutId/create'}
                    params={{ payoutId }}
                    search={{ createdAt }}
                  >
                    {t('Add payment')}
                  </ButtonLink>
                </Stack>
              </TableCell>
            </TableRow>
          )}
          {payoutQuery.isPending &&
            Array.from({ length: 3 }).map((_, rowIndex) => (
              <TableRow key={rowIndex} sx={{ height: '40px' }}>
                {Array.from({ length: editable ? 7 : 6 }).map((__, cellIndex) => (
                  <TableCell key={cellIndex}>
                    <Skeleton variant="text" width={'50%'} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          {payoutQuery.data?.transfers.map((transfer, index) => (
            <TableRow key={transfer.id}>
              <TableCell>{`#${index + 1}`}</TableCell>
              <TableCell>{transfer.fromAccount?.name}</TableCell>
              <TableCell>{getTo(transfer)}</TableCell>
              <TableCell>
                <Money value={mapStrangeServerMoneyToServerMoney(transfer.amount)} />
              </TableCell>
              <TableCell>
                <TransferState status={transfer.status} />
              </TableCell>
              <TableCell>
                <DateTime value={transfer.createdAt} format="date" />
              </TableCell>
              {editable && (
                <TableCell>
                  <Tooltip title={t('Delete')}>
                    <IconButton
                      aria-label={t('Delete')}
                      onClick={() => setSelectedTransferToDelete(transfer)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <DeleteTransferDialog
        transfer={selectedTransferToDelete}
        onClose={() => setSelectedTransferToDelete(null)}
      />
    </>
  );
}
